import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Checkbox, Chip, CircularProgress, FormControl, FormHelperText, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from "@mui/material";
import axios from "axios";
import { Auth } from "aws-amplify";
import { isReadingPassage, isReadingLetter, isReadingWord, isReadingNumber, isListeningPassage, hasComprehensionFlag, hasHumanInputFlag } from "../../../utils/storyType";

const API_URL = "https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com"

const AddStories = ({ data, setData, registerValidator, registerSubmitter, masterAdminInfo, organization, taskJobs }) => {
  const [loadingStories, setLoadingStories] = useState(false);
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const hasActiveTask = taskJobs.some(
    (task) => task.status !== 1
  );

  const grades = useMemo(() => ["1", "2", "3", "4", "5", "6", "7", "8"], []);

  const languages = masterAdminInfo?.allLangs || {};

  const assignedStoriesFetchRef = useRef(false);

  const fetchAssignedStories = useCallback(async () => {
    if (data.assignedStoriesLoaded || assignedStoriesFetchRef.current) return;
    assignedStoriesFetchRef.current = true;

    try {
      const session = await Auth.currentSession();
      const jwtToken = session.idToken.jwtToken;
      const masterAdminId = masterAdminInfo.masterAdmin.masterAdminId;

      const response = await axios.get(
        `${API_URL}/alpha/masterAdmin/${masterAdminId}/stories?org=${organization.prefix}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );

      setData({
        ...data,
        assignedStories: response.data?.stories || [],
        assignedStoriesLoaded: true,
      });
    } catch (error) {
      console.error(error);

      setErrors({
        msg: error?.response?.data?.message || "Unable to fetch assigned stories.",
      });

      assignedStoriesFetchRef.current = false;
    }
  }, [data, masterAdminInfo, organization.prefix, setData]);

  useEffect(() => {
    fetchAssignedStories();
  }, [fetchAssignedStories]);

  const fetchStories = useCallback(async (std, lang) => {
    if (!std || !lang) return;

    // Don't fetch again if this std + language is already cached
    if (data.loaded && data.std === std && data.lang === lang) return;

    try {
      setLoadingStories(true);
      setErrors({});

      const session = await Auth.currentSession();
      const jwtToken = session.idToken.jwtToken;
      const masterAdminId = masterAdminInfo.masterAdmin.masterAdminId

      const response = await axios.get(
        `${API_URL}/alpha/masterAdmin/${masterAdminId}/cms/stories?lang=${lang}&std=${std}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );

      const stories = response.data?.stories || [];

      setData({
        ...data,
        std,
        lang,
        items: stories,
        selectedStoryIds: [],
        loaded: true,
      });
    } catch (error) {
      console.error(error);

      setErrors({
        msg: error?.response?.data?.message || "Unable to fetch stories.",
      });
    } finally {
      setLoadingStories(false);
    }
  }, [data, setData, masterAdminInfo]);

  useEffect(() => {
    if (data.std && data.lang) {
      fetchStories(data.std, data.lang);
    }
  }, [data.std, data.lang, fetchStories]);

  const handleGradeChange = useCallback((std) => {
    setData({
      ...data,
      std,
      items: [],
      selectedStoryIds: [],
      loaded: false,
    });
    
    setPage(0);
    setErrors({});
  }, [data, setData]);

  const handleLangChange = useCallback((lang) => {
    setData({
      ...data,
      lang,
      items: [],
      selectedStoryIds: [],
      loaded: false,
    });

    setPage(0);
    setErrors({});
  }, [data, setData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setData({
      ...data,
      selectedStoryIds: [],
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setData({
      ...data,
      selectedStoryIds: [],
    });
  };

  const isStoryAssigned = useCallback((storyId, std) => {
    const story = data.assignedStories?.find(
      (item) => item.storyId === storyId
    );

    return story?.std?.includes(Number(std)) || false;
  }, [data.assignedStories]);

  const paginatedStories = data.items.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleStorySelection = useCallback((storyId) => {
    if (isStoryAssigned(storyId, data.std)) return;

    const selectedStoryIds = data.selectedStoryIds || [];

    const updatedSelection = selectedStoryIds.includes(storyId)
      ? selectedStoryIds.filter((id) => id !== storyId)
      : [...selectedStoryIds, storyId];

    setData({
      ...data,
      selectedStoryIds: updatedSelection,
    });
  }, [data, setData, isStoryAssigned]);

  const selectableStories = paginatedStories.filter(
    (story) => !isStoryAssigned(story.storyid, data.std)
  );

  const allSelected =
    selectableStories.length > 0 &&
    selectableStories.every((story) =>
      data.selectedStoryIds?.includes(story.storyid)
    );

  const someSelected =
    selectableStories.some((story) =>
      data.selectedStoryIds?.includes(story.storyid)
    ) && !allSelected;

  const handleSelectAll = useCallback(() => {
    const selectableStoryIds = paginatedStories
      .filter((story) => !isStoryAssigned(story.storyid, data.std))
      .map((story) => story.storyid);

    const updatedSelection = allSelected
      ? (data.selectedStoryIds || []).filter(
          (id) => !selectableStoryIds.includes(id)
        )
      : [
          ...(data.selectedStoryIds || []),
          ...selectableStoryIds.filter(
            (id) => !(data.selectedStoryIds || []).includes(id)
          ),
        ];

    setData({
      ...data,
      selectedStoryIds: updatedSelection,
    });
  }, [allSelected, data, paginatedStories, isStoryAssigned, setData]);

  const validateStep3 = useCallback(() => {
    const newErrors = {};

    if (!data.std) {
      newErrors.std = "Grade is required";
    }

    if (!data.lang) {
      newErrors.lang = "Language is required";
    }

    if (data.loaded && data.items.length === 0) {
      newErrors.msg = "No stories found for the selected grade and language.";
    }

    if (!data.selectedStoryIds?.length) {
      newErrors.msg = "Please select at least one story.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [data]);

  useEffect(() => {
    registerValidator(validateStep3);
  }, [registerValidator, validateStep3]);

  const submitStep3 = useCallback(async () => {
    try {
      const session = await Auth.currentSession();
      const jwtToken = session.idToken.jwtToken;

      const payload = {
        orgPrefix: organization.prefix,
        std: data.std,
        lang: data.lang,
        storyIds: data.selectedStoryIds,
      };

      const masterAdminId = masterAdminInfo.masterAdmin.masterAdminId
      const response = await axios.post(
        `${API_URL}/alpha/masterAdmin/${masterAdminId}/stories`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );

      if (response.data?.status !== "success") {
        const responseData = response?.data;
        const backendMessage = responseData?.message || "Unable to create stories task.";
        const backendErrors = responseData?.errors || [];

        setErrors({
          msg: backendMessage,
          errorsList: backendErrors
        });

        return false;
      }

      // Keep the fetched stories cached, only clear the selection.
      setData({
        ...data,
        selectedStoryIds: [],
        assignedStoriesLoaded: false,
      });

      return true;
    } catch (err) {
      console.error(err);
      const responseData = err?.response?.data;
      const backendMessage = responseData?.message || "Unable to create stories task.";
      const backendErrors = responseData?.errors || [];

      setErrors({
        msg: backendMessage,
        errorsList: backendErrors
      });

      return false;
    }
  }, [data, organization.prefix, masterAdminInfo, setData]);

  useEffect(() => {
    registerSubmitter(submitStep3);
  }, [registerSubmitter, submitStep3]);

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Select a grade and language to view the available stories. Select one or more stories and click <strong>Add Stories</strong>.
      </Alert>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 0fr))",
          gap: "20px",
          marginBottom: "20px",
        }}
      >
        <FormControl size="small" fullWidth error={!!errors.lang}>
          <InputLabel>Language</InputLabel>
          <Select
            value={data.lang || ""}
            label="Language"
            onChange={(e) => handleLangChange(e.target.value)}
          >
            {Object.keys(languages).sort().map((lang) => (
              <MenuItem key={lang} value={lang}>
                {languages[lang]}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.lang}</FormHelperText>
        </FormControl>
        <FormControl size="small" fullWidth error={!!errors.std}>
          <InputLabel>Grade</InputLabel>
          <Select
            value={data.std || ""}
            label="Grade"
            onChange={(e) => handleGradeChange(e.target.value)}
          >
            {grades.map((std) => (
              <MenuItem key={std} value={std}>
                {std}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.std}</FormHelperText>
        </FormControl>
      </div>

      {loadingStories && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "2rem",
          }}
        >
          <CircularProgress />
        </div>
      )}

      {!loadingStories && data.loaded && data.items.length === 0 && (
        <Alert severity="warning">
          No stories found for {languages[data.lang] || data.lang} - Grade {data.std}.
        </Alert>
      )}

      {!loadingStories && data.items.length > 0 && (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <h3 style={{ margin: 0 }}>
              Available Stories ({data.items.length})
            </h3>

            <span
              style={{
                fontSize: "0.9rem",
                color: "#666",
              }}
            >
              {data.selectedStoryIds?.length || 0} selected
            </span>
          </div>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <strong>Title</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Level</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Para Count</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Type</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Platform</strong>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedStories.map((story) => {
                  const isAssigned = isStoryAssigned(story.storyid, data.std);
                  const isSelected = isAssigned || data.selectedStoryIds?.includes(story.storyid);

                  return (
                    <TableRow
                      key={story.storyid}
                      hover
                      selected={isSelected}
                      onClick={() => {
                        if (!isAssigned) {
                          handleStorySelection(story.storyid);
                        }
                      }}
                      sx={{
                        cursor: isAssigned ? "default" : "pointer",
                        opacity: isAssigned ? 0.6 : 1,
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          disabled={isAssigned}
                          onChange={() => handleStorySelection(story.storyid)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <h3>{story.title || "-"}</h3>
                        <p style={{fontSize: "12px"}}>ID: {story.storyid}</p>
                        {isAssigned && (
                          <Chip
                            label="Already Added"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            story.level === 1 ? "Easy" : 
                            story.level === 2 ? "Medium" : 
                            story.level === 3 ? "Hard" : 
                            story.level
                          }
                          color={
                            story.level === 1 ? "success" : 
                            story.level === 2 ? "warning" : 
                            story.level === 3 ? "error" : 
                            "default"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {story.paraCount}
                      </TableCell>
                      <TableCell align="center">
                        <>
                          <Chip
                            label={
                              isReadingPassage(story.type) ? "ORF": 
                              isReadingLetter(story.type) ? "Letter" : 
                              isReadingWord(story.type) ? "Word" : 
                              isReadingNumber(story.type) ? "Number" :
                              isListeningPassage ? "Listening" :
                              "Unknown"
                            }
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                          {hasComprehensionFlag(story.type) &&
                            <Chip
                              label="Comprehension"
                              color="secondary"
                              size="small"
                              variant="outlined"
                              sx={{ml: 0.5}}
                            />
                          }
                          {hasHumanInputFlag(story.type) &&
                            <Chip
                              label="Human Input"
                              color="warning"
                              size="small"
                              variant="outlined"
                              sx={{ml: 0.5}}
                            />
                          }
                        </>
                        <p style={{fontSize: "12px"}}>Value: {story.type}</p>
                      </TableCell>
                      <TableCell align="center">
                        {story.platform}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={data.items.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 30]}
            />
          </TableContainer>
        </>
      )}
      {hasActiveTask && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Another task is already processing!, previous task must be fully completed before starting a new one
        </Alert>
      )}
      {errors.msg && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {Array.isArray(errors.errorsList) ?
            <>            
              <h3>{errors.msg}</h3> 
              {errors.errorsList.map((e, i) => (
                <p key={i} style={{marginTop: "0.5rem"}}>{e}</p>
              ))}
            </>
          : 
            <h3>{errors.msg}</h3>
          }
        </Alert>
      )}
    </>
  );
};

export default AddStories;